import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import type { IOpenRecord } from "commonComponents/utilities/MainNavigation/SearchDialog/entity/pageContext";
import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";
import { SEARCH_API_VERSION } from "commonComponents/utilities/MainNavigation/SearchDialog/search/searchApiVersion";
import { useAuthToken } from "commonComponents/utilities/MainNavigation/SearchDialog/search/useAuthToken";

import {
    type IAgentAnswer,
    type IAgentHandleRecord,
    type IAgentToolCall,
} from "./agentAnswer.types";
import { type IAgentAnswerAdapter, liveAgentAnswerAdapter } from "./agentAnswerAdapter";
import { type AgentFailure, describeAgentFailure } from "./agentAnswerFields";
import { readAgentStream } from "./agentStream";

/** Completed turns kept for the transcript above the live answer. */
const MAX_HISTORY = 5;

/**
 * The kinds of record Ask hands the agent, by the service's name for each.
 * An allowlist: get_record answers 404 for a kind it has no spec for, and
 * most of ours (job, changeorder, dailylog) are not its names. A kind added
 * here needs its service name, and ids that match the service's.
 */
const AGENT_RECORD_KINDS = new Map([["job", "jobsite"]]);

/**
 * The question with the open record in front of it, in the block the
 * service's prompt resolves "this" against. In the text because the stream
 * route reads no field for it yet.
 */
function withOpenRecord(question: string, kind: string, id: string): string {
    return [
        "[USER'S CURRENT PAGE — use if the question refers to it, ignore otherwise]",
        `open_record: ${kind} ${id}`,
        "",
        "[QUESTION]",
        question,
    ].join("\n");
}

export interface IAskContext {
    /** The record the question is about, captured on the page it was asked from. */
    openRecord?: IOpenRecord | null;
}

/**
 * Whether this page load has primed the agent. Module-level: the dialog
 * remounts on every open and the Ask page is a second caller.
 */
let warmedThisPage = false;

/** Each test is its own page load. */
export function resetWarmedForTests(): void {
    warmedThisPage = false;
}

/**
 * How long a turn may go without an event before the client gives up.
 *
 * Counted from the last event, so a long turn still reporting progress runs
 * on. Keepalive pings do not count: a stalled engine keeps sending them.
 */
export const AGENT_SILENCE_TIMEOUT_MS = 90_000;

/** Carries the failure out of the turn, so the copy is chosen in one place. */
class AgentTurnError extends Error {
    readonly failure: AgentFailure;

    constructor(failure: AgentFailure) {
        super(failure.kind);
        this.failure = failure;
    }
}

/**
 * What went wrong, from whatever the turn threw. The browser's own messages
 * ("Failed to fetch", "network error") are for developers, not for the page.
 */
function failureOf(error: unknown, streamOpened: boolean): AgentFailure {
    if (error instanceof AgentTurnError) {
        return error.failure;
    }
    if (streamOpened) {
        return { kind: "cut" };
    }
    // What `fetch` rejects with when no response came back at all.
    return error instanceof TypeError ? { kind: "offline" } : { kind: "status", status: 0 };
}

type HandleTable = Record<string, IAgentHandleRecord>;

/** A conversation as a page keeps it across a reload. JSON-safe throughout. */
export interface IAgentTranscript {
    sessionId: string | null;
    /** Every handle the live session has sent, which the next turn merges into. */
    handles: HandleTable;
    /**
     * Each answered turn's own table, by turn id, so a restored chip still
     * resolves to its own record after a session that restarted numbering.
     */
    turnHandles: Record<string, HandleTable>;
    /** Oldest first; the last one is the most recent turn. */
    turns: readonly IAgentAnswer[];
}

/** Keeps a table for each turn still on screen: the history plus the live one. */
function rememberHandles(tables: Map<string, HandleTable>, turnId: string, table: HandleTable) {
    tables.set(turnId, table);
    for (const oldest of tables.keys()) {
        if (tables.size <= MAX_HISTORY + 1) {
            break;
        }
        tables.delete(oldest);
    }
}

interface IAgentState {
    answer: IAgentAnswer | null;
    history: readonly IAgentAnswer[];
    isBusy: boolean;
}

/** POSTs to the search service's agent routes with a service token. */
function useAgentPost() {
    const searchConfig = useContext(AppDefaultInfoContext)?.searchServiceConfiguration;
    const searchBaseUrl = searchConfig?.apiBaseUrl ?? "";
    const { getToken, clearToken } = useAuthToken(searchConfig?.authAudience ?? "");
    const available = searchBaseUrl.length > 0;

    const post = useCallback(
        async (path: string, body: unknown, signal: AbortSignal): Promise<Response> => {
            const token = await getToken().catch(() => "");
            return fetch(`${searchBaseUrl}${SEARCH_API_VERSION}${path}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(body),
                signal,
            });
        },
        [getToken, searchBaseUrl],
    );

    return { available, post, clearToken };
}

/**
 * Primes the agent's process, this tenant's index and the prompt cache on
 * mount, once per page load. Fire-and-forget; a failed warm-up is unlatched so
 * the next caller retries.
 */
export function useWarmSearchAgent(): void {
    const { available, post } = useAgentPost();

    useEffect(() => {
        if (!available || warmedThisPage) {
            return;
        }
        warmedThisPage = true;
        const unlatch = () => {
            warmedThisPage = false;
        };
        void post("/agent/warm", { prime_model: true }, new AbortController().signal).then(
            (response) => {
                if (!response.ok) {
                    unlatch();
                }
            },
            unlatch,
        );
    }, [available, post]);
}

export interface IUseSearchAgentOptions {
    /** Injected in tests; defaults to the live reducers. */
    adapter?: IAgentAnswerAdapter;
    /** Injected in tests; defaults to {@link AGENT_SILENCE_TIMEOUT_MS}. */
    silenceTimeoutMs?: number;
    /** A conversation to carry on, read once on mount. */
    restore?: IAgentTranscript | null;
    /** Told whenever the transcript changes. */
    onTranscriptChange?: (transcript: IAgentTranscript) => void;
    /** Told about each tool call as the stream reports it, with the turn it belongs to. */
    onToolCall?: (turnId: string, call: IAgentToolCall) => void;
}

export interface IUseSearchAgentResult {
    /** The live or most recent turn, or null before the first question. */
    answer: IAgentAnswer | null;
    /** Completed turns, oldest first. */
    history: readonly IAgentAnswer[];
    isBusy: boolean;
    /** False with no service configured — ask() is then a no-op. */
    available: boolean;
    /**
     * Replaces a turn still running, which ends as aborted rather than hanging.
     * The context goes with this turn only; the session keeps it for follow-ups.
     */
    ask: (question: string, context?: IAskContext) => Promise<void>;
    /** Asks the live turn's question again, with its context, in its place. */
    retry: () => Promise<void>;
    cancel: () => void;
    /** Drops the session, handles, and history. */
    reset: () => void;
    /**
     * The one gate a citation chip passes through. Null when unknown.
     *
     * Pass the chip's turn: handles are numbered per session, so once a new
     * session starts, its `r1` is not an earlier turn's `r1`.
     */
    resolveHandle: (handle: string, turnId?: string) => ISearchHit | null;
    /**
     * The live session, which Stop, a timeout or a lost session replace. For
     * telemetry about a turn, read that turn's own `sessionId` instead.
     */
    getSessionId: () => string | null;
}

/**
 * Runs one agent turn against the server's own orchestration.
 *
 * The server drives the tool loop: this mints a session, opens
 * `/agent/stream`, and renders the progress it reports.
 *
 * Prefer mounting this ABOVE anything that unmounts on close: the session and
 * the accumulated handle table live in refs, so tearing it down mid-
 * conversation starts a new session and strands every citation chip on screen.
 * Unmounting mid-turn is safe regardless — the cleanup below closes the stream
 * rather than leaving it reading into a dead component.
 */
export function useSearchAgent(options: IUseSearchAgentOptions): IUseSearchAgentResult {
    const {
        adapter = liveAgentAnswerAdapter,
        silenceTimeoutMs = AGENT_SILENCE_TIMEOUT_MS,
        restore,
        onTranscriptChange,
        onToolCall,
    } = options;

    const { available, post, clearToken } = useAgentPost();

    // One state object so a turn moving into history and the next one starting
    // happen in a single pure update.
    const [{ answer, history, isBusy }, setState] = useState<IAgentState>(() => ({
        answer:
            restore && restore.turns.length > 0 ? restore.turns[restore.turns.length - 1] : null,
        history: restore ? restore.turns.slice(0, -1).slice(-MAX_HISTORY) : [],
        isBusy: false,
    }));
    const updateAnswer = useCallback((update: (previous: IAgentAnswer) => IAgentAnswer) => {
        setState((previous) =>
            previous.answer === null ? previous : { ...previous, answer: update(previous.answer) },
        );
    }, []);

    const sessionRef = useRef<string | null>(restore?.sessionId ?? null);
    /**
     * Every handle seen so far, merged across turns.
     *
     * The envelope's `handles` is this turn's records only, while a transcript
     * keeps older turns on screen — so accumulating here is what stops an
     * earlier chip going inert when the next turn lands.
     */
    const handlesRef = useRef<HandleTable>(restore?.handles ?? {});
    /** What each answered turn's chips resolve against, kept past a new session. */
    const turnHandlesRef = useRef(new Map(Object.entries(restore?.turnHandles ?? {})));
    /** Set only while a turn is in flight. */
    const abortRef = useRef<AbortController | null>(null);
    /** What the live turn was asked with, so a retry sends it again. */
    const liveContextRef = useRef<IAskContext | undefined>(undefined);
    /**
     * Bumped on every ask and cancel. `signal.aborted` alone loses the race
     * where a read resolved just before the abort landed.
     */
    const turnSeqRef = useRef(0);

    const resolveHandle = useCallback((handle: string, turnId?: string): ISearchHit | null => {
        // A turn without a table resolves nothing: another session's `r1`
        // would open the wrong record.
        const table =
            turnId === undefined ? handlesRef.current : turnHandlesRef.current.get(turnId);
        const record = table?.[handle];
        if (!record) {
            return null;
        }
        return {
            entity: record.entity,
            id: record.id,
            score: 0,
            rerank_score: 0,
            reranker: "agent",
            summary: record.summary,
            payload: record.payload,
        };
    }, []);

    /** Aborts an in-flight turn on unmount. Refs only: no state updates on the way out. */
    useEffect(
        () => () => {
            abortRef.current?.abort();
            abortRef.current = null;
            turnSeqRef.current += 1;
        },
        [],
    );

    /** Refs only. Answered turns keep their own handle tables. */
    const dropSession = useCallback(() => {
        sessionRef.current = null;
        handlesRef.current = {};
    }, []);

    /**
     * Stops the turn in flight and says whether there was one.
     *
     * The session goes with it: the server finishes a turn the client hung up
     * on and keeps it, so the next question on that session got both answered.
     */
    const stopTurn = useCallback((): boolean => {
        const controller = abortRef.current;
        if (controller === null) {
            return false;
        }
        controller.abort();
        abortRef.current = null;
        turnSeqRef.current += 1;
        dropSession();
        return true;
    }, [dropSession]);

    const cancel = useCallback(() => {
        if (!stopTurn()) {
            return;
        }
        // An aborted turn is not appended to history: the user dismissed it.
        setState((previous) => ({
            ...previous,
            isBusy: false,
            answer:
                previous.answer === null
                    ? null
                    : adapter.finish(previous.answer, { phase: "aborted", error: null }),
        }));
    }, [adapter, stopTurn]);

    const reset = useCallback(() => {
        stopTurn();
        dropSession();
        turnHandlesRef.current.clear();
        setState({ answer: null, history: [], isBusy: false });
    }, [stopTurn, dropSession]);

    // Read from refs rather than tracked: each is updated just before the
    // state change that follows it, so the write after a turn lands has all three.
    useEffect(() => {
        onTranscriptChange?.({
            sessionId: sessionRef.current,
            handles: handlesRef.current,
            turnHandles: Object.fromEntries(turnHandlesRef.current),
            turns: answer === null ? history : [...history, answer],
        });
    }, [answer, history, onTranscriptChange]);

    /**
     * Opens a turn's stream, recovering once from each rejection a page left
     * open runs into: a token the server stopped accepting, and a session it
     * no longer has.
     */
    const openStream = useCallback(
        async (turn: { title: string; text: string }, signal: AbortSignal): Promise<Response> => {
            const { title, text } = turn;
            let reauthorized = false;
            let restarted = false;
            for (;;) {
                if (sessionRef.current === null) {
                    const created = await post("/agent/new", { title }, signal);
                    if (created.status === 401 && !reauthorized) {
                        reauthorized = true;
                        clearToken();
                        continue;
                    }
                    if (!created.ok) {
                        throw new AgentTurnError({ kind: "status", status: created.status });
                    }
                    const body = (await created.json()) as { session_id?: string };
                    sessionRef.current = body.session_id ?? null;
                }

                const streamed = await post(
                    "/agent/stream",
                    { session_id: sessionRef.current, text },
                    signal,
                );
                if (streamed.status === 401 && !reauthorized) {
                    reauthorized = true;
                    clearToken();
                    continue;
                }
                // "Unknown session": the server restarted or evicted it.
                if (streamed.status === 404) {
                    dropSession();
                    if (!restarted) {
                        restarted = true;
                        continue;
                    }
                }
                if (!streamed.ok) {
                    throw new AgentTurnError({ kind: "status", status: streamed.status });
                }
                return streamed;
            }
        },
        [post, clearToken, dropSession],
    );

    const runTurn = useCallback(
        async (question: string, replacesLive: boolean, context?: IAskContext): Promise<void> => {
            const trimmed = question.trim();
            if (!available || trimmed.length === 0) {
                return;
            }
            liveContextRef.current = context;

            const interrupted = stopTurn();
            const controller = new AbortController();
            abortRef.current = controller;
            turnSeqRef.current += 1;
            const turnSeq = turnSeqRef.current;
            const turnId = uuidv4();
            const isStale = () => turnSeq !== turnSeqRef.current || controller.signal.aborted;

            const settle = (failure: AgentFailure | null) => {
                if (isStale()) {
                    return;
                }
                abortRef.current = null;
                setState((previous) => ({
                    ...previous,
                    isBusy: false,
                    answer:
                        previous.answer === null
                            ? null
                            : adapter.finish(
                                  previous.answer,
                                  failure === null
                                      ? { phase: "done", error: null }
                                      : { phase: "error", error: describeAgentFailure(failure) },
                              ),
                }));
            };

            let silence: ReturnType<typeof setTimeout> | undefined;
            const heardFrom = () => {
                clearTimeout(silence);
                silence = setTimeout(() => {
                    // Settled first: stopping bumps the sequence, after which
                    // this turn could no longer write its own outcome.
                    if (abortRef.current === controller) {
                        settle({ kind: "timeout" });
                        dropSession();
                        controller.abort();
                    }
                }, silenceTimeoutMs);
            };

            // Moves to history only once the next turn starts, so it stays
            // readable until then. A turn cut short by this one is finished as
            // aborted, as a cancelled one is, rather than left thinking.
            setState((previous) => {
                const outgoing =
                    previous.answer !== null && interrupted
                        ? adapter.finish(previous.answer, { phase: "aborted", error: null })
                        : previous.answer;
                const keepsOutgoing =
                    outgoing !== null && outgoing.phase !== "aborted" && !replacesLive;
                return {
                    answer: adapter.begin({ turnId, question: trimmed }),
                    history: keepsOutgoing
                        ? [...previous.history, outgoing].slice(-MAX_HISTORY)
                        : previous.history,
                    isBusy: true,
                };
            });
            heardFrom();

            const record = context?.openRecord ?? null;
            const recordKind = record ? AGENT_RECORD_KINDS.get(record.entity) : undefined;
            const text =
                record !== null && recordKind !== undefined
                    ? withOpenRecord(trimmed, recordKind, record.id)
                    : trimmed;

            let streamOpened = false;
            try {
                // Titled by the question alone, as the transcript shows it.
                const streamed = await openStream(
                    { title: trimmed.slice(0, 60), text },
                    controller.signal,
                );
                streamOpened = true;
                heardFrom();
                const sessionId = sessionRef.current;
                updateAnswer((previous) =>
                    previous.turnId === turnId ? { ...previous, sessionId } : previous,
                );

                let sawFinal = false;
                let streamFailure: AgentFailure | null = null;

                for await (const event of readAgentStream(streamed)) {
                    if (isStale()) {
                        return;
                    }
                    heardFrom();

                    if (event.type === "tool_start") {
                        const call: IAgentToolCall = {
                            id: event.call_id,
                            name: event.tool,
                            input: event.input,
                            label: event.label,
                        };
                        updateAnswer((previous) => adapter.onToolCall(previous, call));
                        onToolCall?.(turnId, call);
                    } else if (event.type === "tool_end") {
                        updateAnswer((previous) =>
                            adapter.onToolEnd(previous, { callId: event.call_id, ms: event.ms }),
                        );
                    } else if (event.type === "final") {
                        sawFinal = true;
                        // Merged, not replaced: this turn's records join the
                        // ones earlier turns' chips still point at.
                        handlesRef.current = {
                            ...handlesRef.current,
                            ...(event.envelope.handles ?? {}),
                        };
                        rememberHandles(turnHandlesRef.current, turnId, handlesRef.current);

                        const finalInput = {
                            text: event.envelope.text ?? "",
                            handles: handlesRef.current,
                        };
                        updateAnswer((previous) => adapter.onFinal(previous, finalInput));
                    } else if (event.type === "error") {
                        streamFailure = { kind: "status", status: event.status };
                        // Lost after the owner check passed. Dropped here so
                        // the next question starts a session instead of
                        // failing the same way.
                        if (event.status === 404) {
                            dropSession();
                        }
                    }
                    // `text` events are ignored: `final.text` excludes pre-tool
                    // reasoning the server filters out, and they would show it.
                }

                settle(
                    streamFailure ??
                        // The body ended with no `final`: a dropped
                        // connection, not an answer.
                        (sawFinal ? null : { kind: "cut" }),
                );
            } catch (error) {
                if ((error as Error)?.name === "AbortError" || isStale()) {
                    return;
                }
                settle(failureOf(error, streamOpened));
            } finally {
                clearTimeout(silence);
            }
        },
        [
            available,
            adapter,
            stopTurn,
            dropSession,
            openStream,
            silenceTimeoutMs,
            onToolCall,
            updateAnswer,
        ],
    );

    const ask = useCallback(
        (question: string, context?: IAskContext) => runTurn(question, false, context),
        [runTurn],
    );

    // A failed turn is replaced by its retry rather than kept above it.
    const liveQuestion = answer?.question ?? null;
    const retry = useCallback(
        () =>
            liveQuestion === null
                ? Promise.resolve()
                : runTurn(liveQuestion, true, liveContextRef.current),
        [liveQuestion, runTurn],
    );

    const getSessionId = useCallback(() => sessionRef.current, []);

    return {
        answer,
        history,
        isBusy,
        available,
        ask,
        retry,
        cancel,
        reset,
        resolveHandle,
        getSessionId,
    };
}
